/**
 * Admin authentication utilities for frontend
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Check if current user is authenticated as admin
 */
export async function checkAdminAuth() {
  try {
    const response = await fetch(`${API_BASE}/api/admin/me`, {
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    return null;
  } catch (error) {
    console.error("Admin auth check failed:", error);
    return null;
  }
}

/**
 * Admin login
 */
export async function adminLogin(email, password) {
  try {
    const response = await fetch(`${API_BASE}/api/admin/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, error: data.detail?.message || "Login failed" };
    }
  } catch (error) {
    console.error("Admin login failed:", error);
    return { success: false, error: "Network error" };
  }
}

/**
 * Admin logout
 */
export async function adminLogout() {
  try {
    const response = await fetch(`${API_BASE}/api/admin/signout`, {
      method: "POST",
      credentials: "include",
    });

    return response.ok;
  } catch (error) {
    console.error("Admin logout failed:", error);
    return false;
  }
}

/**
 * Extend admin session
 */
export async function extendAdminSession() {
  try {
    const response = await fetch(`${API_BASE}/api/admin/extend`, {
      method: "POST",
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      return { success: false, error: "Session extension failed" };
    }
  } catch (error) {
    console.error("Session extension failed:", error);
    return { success: false, error: "Network error" };
  }
}

/**
 * Get admin dashboard stats
 */
export async function getAdminStats() {
  try {
    const response = await fetch(`${API_BASE}/api/admin/stats`, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    }

    throw new Error("Failed to fetch admin stats");
  } catch (error) {
    console.error("Get admin stats failed:", error);
    throw error;
  }
}

/**
 * Get contractors list
 */
export async function getContractors() {
  try {
    const response = await fetch(`${API_BASE}/api/admin/contractors`, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    }

    throw new Error("Failed to fetch contractors");
  } catch (error) {
    console.error("Get contractors failed:", error);
    throw error;
  }
}

/**
 * Update contractor
 */
export async function updateContractor(contractorId, updates) {
  try {
    const response = await fetch(
      `${API_BASE}/api/admin/contractors/${contractorId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(updates),
      },
    );

    if (response.ok) {
      return await response.json();
    }

    throw new Error("Failed to update contractor");
  } catch (error) {
    console.error("Update contractor failed:", error);
    throw error;
  }
}

/**
 * Get alerts list
 */
export async function getAlerts() {
  try {
    const response = await fetch(`${API_BASE}/api/admin/alerts`, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    }

    throw new Error("Failed to fetch alerts");
  } catch (error) {
    console.error("Get alerts failed:", error);
    throw error;
  }
}

/**
 * Get audit logs
 */
export async function getAuditLogs(limit = 50) {
  try {
    const response = await fetch(
      `${API_BASE}/api/admin/audit-logs?limit=${limit}`,
      {
        credentials: "include",
      },
    );

    if (response.ok) {
      return await response.json();
    }

    throw new Error("Failed to fetch audit logs");
  } catch (error) {
    console.error("Get audit logs failed:", error);
    throw error;
  }
}
